/**
 * Offer facts (SEO audit 2026-10-25 §4 item 3): the three things a shopper checks before using a code, stored on the offer
 * (`Coupon.facts`, JSONB) and shown as one small muted row under the offer text: "Applies to: Pro plan · For: new customers · Billing: annual".
 *
 *   appliesTo  the plan or product the offer is for, at most 60 characters
 *   audience   who may use it: "new customers" | "existing customers" | "all customers" | "members" | "students", or free text up to 40
 *   billing    the billing term it needs ("annual", "monthly", "24-month term"), at most 40
 *
 * Written only through the SwiftPilot API (strict: `parseOfferFacts` rejects unknown keys and bad values). Read from the database
 * leniently (`readOfferFacts`): whatever is not a clean value is dropped, so a bad row can never break or pollute a page.
 * Pure; no coupon code is ever part of it (the API also runs the public-text code guard over it).
 */

export const OFFER_FACT_KEYS = ["appliesTo", "audience", "billing"] as const;
export type OfferFactKey = (typeof OFFER_FACT_KEYS)[number];

export const OFFER_FACT_MAX: Record<OfferFactKey, number> = { appliesTo: 60, audience: 40, billing: 40 };

/** The usual audiences; any other text up to 40 characters is accepted too. */
export const OFFER_AUDIENCES = ["new customers", "existing customers", "all customers", "members", "students"] as const;

export interface OfferFacts {
    appliesTo?: string;
    audience?: string;
    billing?: string;
}

export class OfferFactsError extends Error {
    constructor(public code: "invalid_facts" | "unknown_fact", message: string) { super(message); }
}

// No markup and no control characters in a fact: it is one short plain-text phrase.
const UNSAFE = /[<>\u0000-\u001f\u007f]/;

function clean(value: unknown, key: OfferFactKey): string | null {
    if (typeof value !== "string") return null;
    const text = value.replace(/\s+/g, " ").trim();
    if (!text || text.length > OFFER_FACT_MAX[key] || UNSAFE.test(text)) return null;
    return text;
}

/**
 * Strict validation for a write. `null` (or an object with no value left) clears the facts and returns null; otherwise the
 * normalised object. Throws OfferFactsError for a non-object, an unknown key, or a value that is not a clean string within its limit.
 */
export function parseOfferFacts(input: unknown): OfferFacts | null {
    if (input === null) return null;
    if (typeof input !== "object" || Array.isArray(input)) throw new OfferFactsError("invalid_facts", "facts must be an object or null");
    const out: OfferFacts = {};
    for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
        if (!(OFFER_FACT_KEYS as readonly string[]).includes(key)) throw new OfferFactsError("unknown_fact", `Unknown facts key: ${key}`);
        const k = key as OfferFactKey;
        // An explicit null/undefined/empty string for a key means "no value for it".
        if (value === null || value === undefined || (typeof value === "string" && !value.trim())) continue;
        const text = clean(value, k);
        if (text === null) throw new OfferFactsError("invalid_facts", `facts.${k} must be plain text of at most ${OFFER_FACT_MAX[k]} characters`);
        out[k] = text;
    }
    return Object.keys(out).length ? out : null;
}

/** Lenient read of a stored value (database or props): the clean parts only, undefined when nothing is left. */
export function readOfferFacts(stored: unknown): OfferFacts | undefined {
    if (typeof stored !== "object" || stored === null || Array.isArray(stored)) return undefined;
    const out: OfferFacts = {};
    for (const key of OFFER_FACT_KEYS) {
        const text = clean((stored as Record<string, unknown>)[key], key);
        if (text) out[key] = text;
    }
    return Object.keys(out).length ? out : undefined;
}

/** The row's parts in display order, e.g. [["Applies to","Pro plan"],["For","new customers"]]; empty = render no row. */
export function offerFactParts(facts: OfferFacts | null | undefined): Array<[label: string, value: string]> {
    const f = readOfferFacts(facts);
    if (!f) return [];
    const parts: Array<[string, string]> = [];
    if (f.appliesTo) parts.push(["Applies to", f.appliesTo]);
    if (f.audience) parts.push(["For", f.audience]);
    if (f.billing) parts.push(["Billing", f.billing]);
    return parts;
}

/** The row as one string, or null when there is nothing to show. */
export function offerFactsLine(facts: OfferFacts | null | undefined): string | null {
    const parts = offerFactParts(facts);
    return parts.length ? parts.map(([label, value]) => `${label}: ${value}`).join(" · ") : null;
}
